/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useCallback, useMemo} from 'react';
import {type SxProps} from '@mui/material';
import {type CollectionSubType, Concept} from 'types';
import {useLocalization} from 'localization';
import {ColorPalette} from 'theme';
import {getActualDataChangesWithSchema} from 'services/metadata/functions/getActualDataChangesWithSchema';
import {useGetSeriesQuery} from 'services/series/seriesApi';
import {ActionButton, Card, FlexBox, Text} from 'components';
import {RtkContent} from 'components/rtkContent/RtkContent';
import {DataFormV1} from 'schema';
import {DataFormCompareLayout} from 'schema/form/components/DataFormCompareLayout';
import {useSchema} from 'schemas';
import {useCollectionFormConfiguration} from 'editors/collections/hooks/useCollectionFormConfiguration';

const primaryColorSx: SxProps = {
  color: ColorPalette.primary.burgundy,
};

type Props = {
  seriesId: string;
  otherId: string;
};
export const MergeSeriesSceneCore: React.FC<Props> = ({seriesId, otherId}) => {
  const {t} = useLocalization();
  const {
    data: seriesData,
    status: seriesStatus,
    error: seriesError,
  } = useGetSeriesQuery({
    id: seriesId,
  });
  const {
    data: otherData,
    status: otherStatus,
    error: otherError,
  } = useGetSeriesQuery({
    id: otherId,
  });

  const formConfiguration = useCollectionFormConfiguration();

  const collectionSubtype: CollectionSubType =
    seriesData?.collectionType ?? Concept.series;

  const schema = useSchema({
    entity: Concept.collection,
    subType: collectionSubtype,
  });

  const {actualChanges, trimmedSchema} = useMemo(() => {
    const changes =
      seriesData && otherData
        ? getActualDataChangesWithSchema(
            otherData,
            seriesData,
            schema,
            undefined,
            {
              excludeEmpty: true,
              excludeEqual: true,
              excludeReadOnly: false,
            },
          )
        : undefined;

    return {
      ...changes,
    };
  }, [otherData, seriesData, schema]);

  const onDataChanged = useCallback(() => {}, []);
  const setApprovedValue = useCallback(() => {}, []);

  return (
    <>
      <FlexBox horizontal right width={'100%'}>
        <ActionButton
          title={'Slå sammen serier (ikke implementert)'}
          onClick={() => undefined}
        />
      </FlexBox>
      <FlexBox horizontal pb={1} px={2} flex={1}>
        <DataFormCompareLayout
          renderCurrent={() => (
            <>
              <Text variant="h4" sx={primaryColorSx} textTransform="uppercase">
                Serie som beholdes
              </Text>
              <Text variant="caption" sx={primaryColorSx}>
                Denne serien vil få alle koblingene, og eventuelle endringer som
                gjøres.
              </Text>
            </>
          )}
          renderProposed={() => (
            <>
              <Text variant="h4" sx={primaryColorSx} textTransform="uppercase">
                Serie som slettes
              </Text>
              <Text variant="caption" sx={primaryColorSx}>
                Koblingene vil bli flyttet fra denne serien til den andre
                serien, og denne serien vil bli slettet etterpå.
              </Text>
            </>
          )}
        />
      </FlexBox>
      <FlexBox horizontal pb={1} px={2} flex={1}>
        <DataFormCompareLayout
          renderCurrent={() => (
            <RtkContent status={seriesStatus} error={seriesError}>
              <DataFormV1
                id={'mergedSeriesData'}
                schema={schema}
                originalData={seriesData}
                data={seriesData ?? {}}
                configuration={formConfiguration}
                mode="edit"
                relatedData={{}}
                showErrors={true}
                onDataChanged={onDataChanged}
                setApprovedValue={setApprovedValue}
              />
            </RtkContent>
          )}
          renderProposed={() => (
            <RtkContent status={otherStatus} error={otherError}>
              {trimmedSchema ? (
                <DataFormV1
                  id={'mergedSeriesData'}
                  schema={trimmedSchema}
                  data={actualChanges ?? {}}
                  configuration={formConfiguration}
                  mode="read-only"
                  relatedData={{}}
                />
              ) : (
                <Card>Ingen forskjeller</Card>
              )}
            </RtkContent>
          )}
        />
      </FlexBox>
    </>
  );
};
